// Three.js Renderer setup with post-processing
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';

export class Rendering {
    private static instance: Rendering;

    public renderer!: THREE.WebGLRenderer;
    public scene!: THREE.Scene;
    private domElement: HTMLElement;

    // Post-processing
    private composer!: EffectComposer;
    private bloomPass!: UnrealBloomPass;
    private fxaaPass!: ShaderPass;
    private usePostProcessing: boolean = true;

    private constructor(container: HTMLElement) {
        this.domElement = container;
        this.setupRenderer();
        this.setupScene();
    }

    static init(container: HTMLElement): Rendering {
        if (!Rendering.instance) {
            Rendering.instance = new Rendering(container);
        }
        return Rendering.instance;
    }

    static getInstance(): Rendering {
        return Rendering.instance;
    }

    private setupRenderer(): void {
        console.log('🎨 Rendering.setupRenderer starting...');

        // Check if there are existing canvases
        const existingCanvases = this.domElement.querySelectorAll('canvas');
        console.log(`🎨 Found ${existingCanvases.length} existing canvases in container`);
        existingCanvases.forEach((c, i) => {
            console.log(`   Canvas ${i} className: ${c.className}, id: ${c.id}, width: ${c.width}, height: ${c.height}`);
            // Check context
            const ctx2d = c.getContext('2d');
            console.log(`   Canvas ${i} has 2d context? ${!!ctx2d}`);
        });

        try {
            this.renderer = new THREE.WebGLRenderer({
                antialias: true,
                alpha: true,
                powerPreference: 'high-performance',
            });
            console.log('✅ WebGLRenderer created successfully');
        } catch (e) {
            console.error('❌ Failed to create WebGLRenderer:', e);
        }

        const width = this.domElement.clientWidth;
        const height = this.domElement.clientHeight;

        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.2;

        // Enable shadows
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        this.domElement.appendChild(this.renderer.domElement);
        console.log('🎨 Renderer canvas appended to DOM');
    }

    private setupScene(): void {
        this.scene = new THREE.Scene();

        // Gradient sky background using canvas
        const skyCanvas = document.createElement('canvas');
        skyCanvas.width = 512;
        skyCanvas.height = 512;
        const ctx = skyCanvas.getContext('2d')!;

        // Create gradient - pink to blue sky
        const gradient = ctx.createLinearGradient(0, 0, 0, 512);
        gradient.addColorStop(0, '#87CEEB');    // Light blue at top
        gradient.addColorStop(0.4, '#B8D4E3');  // Lighter in middle
        gradient.addColorStop(0.7, '#FFE4E1');  // Hint of pink
        gradient.addColorStop(1, '#FFF5F5');    // Very light pink at horizon
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 512, 512);

        const skyTexture = new THREE.CanvasTexture(skyCanvas);
        this.scene.background = skyTexture;

        // Lighter fog for depth
        this.scene.fog = new THREE.Fog(0xFFE4E1, 120, 400);
    }

    setupPostProcessing(camera: THREE.Camera): void {
        const width = this.domElement.clientWidth;
        const height = this.domElement.clientHeight;
        const pixelRatio = this.renderer.getPixelRatio();

        // Create composer
        this.composer = new EffectComposer(this.renderer);

        // Render pass
        const renderPass = new RenderPass(this.scene, camera);
        this.composer.addPass(renderPass);

        // Bloom pass for glow effects
        this.bloomPass = new UnrealBloomPass(
            new THREE.Vector2(width, height),
            0.4,  // strength - subtle glow
            0.4,  // radius
            0.9   // threshold - only bright objects glow
        );
        this.composer.addPass(this.bloomPass);

        // FXAA for anti-aliasing
        this.fxaaPass = new ShaderPass(FXAAShader);
        this.fxaaPass.uniforms['resolution'].value.set(
            1 / (width * pixelRatio),
            1 / (height * pixelRatio)
        );
        this.composer.addPass(this.fxaaPass);
    }

    render(camera: THREE.Camera): void {
        if (this.usePostProcessing && this.composer) {
            this.composer.render();
        } else {
            this.renderer.render(this.scene, camera);
        }
    }

    resize(): void {
        const width = this.domElement.clientWidth;
        const height = this.domElement.clientHeight;
        const pixelRatio = this.renderer.getPixelRatio();

        this.renderer.setSize(width, height);

        if (this.composer) {
            this.composer.setSize(width, height);
        }

        if (this.fxaaPass) {
            this.fxaaPass.uniforms['resolution'].value.set(
                1 / (width * pixelRatio),
                1 / (height * pixelRatio)
            );
        }

        if (this.bloomPass) {
            this.bloomPass.setSize(width, height);
        }
    }

    setBloomStrength(strength: number): void {
        if (this.bloomPass) {
            this.bloomPass.strength = strength;
        }
    }

    setBloomRadius(radius: number): void {
        if (this.bloomPass) {
            this.bloomPass.radius = radius;
        }
    }

    togglePostProcessing(): boolean {
        this.usePostProcessing = !this.usePostProcessing;
        return this.usePostProcessing;
    }

    getCanvas(): HTMLCanvasElement {
        return this.renderer.domElement;
    }
}
